import { KeyboardArrowRightIcon } from '@/components/Icons';
import { cn } from '../../../utils/cn';
import type { ConfidentialToken } from '@/hooks/useCofheTokenLists';
import { TokenIcon } from './TokenIcon';
import { CofheTokenConfidentialBalance, BalanceType, CofheTokenPublicBalance } from './CofheTokenConfidentialBalance';
import { useCofheTokenDecryptedBalance, useCoingeckoUsdPrice } from '@/hooks';
import { sepolia } from '@cofhe/sdk/chains';
import { useCofheAccount } from '@/hooks/useCofheConnection';
import { formatUsdAmount } from '@/utils/format';
import { useCofheTokenPublicBalance } from '@/hooks/useCofheTokenPublicBalance';
import { TMP_WBTC_ON_MAINNET } from '@/utils/coingecko';

export const TokenRow: React.FC<{
  token: ConfidentialToken;
  onClick: () => void;
  balanceType: BalanceType;
  topLabel?: string;
}> = ({ token, onClick, topLabel, balanceType }) => {
  const publicSymbol = token.extensions.fhenix.erc20Pair?.symbol ?? token.symbol;
  const destinationSymbol = token.symbol;
  const showDestination =
    balanceType === BalanceType.Public && !!publicSymbol && !!destinationSymbol && publicSymbol !== destinationSymbol;

  const account = useCofheAccount();
  const { data: confidentialBalance } = useCofheTokenDecryptedBalance(
    {
      token,
      accountAddress: account,
    },
    {
      enabled: balanceType === BalanceType.Confidential,
    }
  );

  const { data: publicBalance } = useCofheTokenPublicBalance({ token, accountAddress: account });
  // tmp: TODO: for testnet, fetch the price of WBTC on mainnet to display in the UI
  const { data: price } = useCoingeckoUsdPrice(
    token.chainId === sepolia.id
      ? {
          chainId: TMP_WBTC_ON_MAINNET.chainId,
          tokenAddress: TMP_WBTC_ON_MAINNET.address,
        }
      : {
          chainId: token.chainId,
          tokenAddress: token.extensions.fhenix.erc20Pair?.address ?? token.address,
        }
  );

  const usdValue =
    balanceType === BalanceType.Confidential
      ? confidentialBalance && price && formatUsdAmount(confidentialBalance.unit.multipliedBy(price))
      : publicBalance && price && formatUsdAmount(publicBalance.unit.multipliedBy(price));

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-center justify-between px-3 py-2 rounded-lg',
        'cofhe-hover-overlay transition-colors',
        'cursor-pointer'
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* ConfidentialToken Icon */}
        <TokenIcon logoURI={token.logoURI} alt={token.name} size="sm" />

        {/* ConfidentialToken label */}
        <div className="min-w-0 flex-1">
          {topLabel && <div className="text-xxxs opacity-70 cofhe-text-primary leading-none">{topLabel}</div>}
          <div className="text-sm font-medium cofhe-text-primary truncate leading-tight">
            {balanceType === BalanceType.Confidential ? token.symbol : publicSymbol}
          </div>
          {showDestination && (
            <div className="text-xxxs opacity-70 cofhe-text-primary truncate leading-tight">→ {destinationSymbol}</div>
          )}
        </div>
      </div>

      {/* Balance and Arrow */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="w-24 text-right whitespace-nowrap tabular-nums">
          {balanceType === BalanceType.Confidential ? (
            <CofheTokenConfidentialBalance
              token={token}
              showSymbol={false}
              size="sm"
              decimalPrecision={5}
              className="font-medium inline-block w-full text-right"
            />
          ) : (
            <CofheTokenPublicBalance
              token={token}
              showSymbol={false}
              size="sm"
              decimalPrecision={5}
              className="font-medium inline-block w-full text-right"
            />
          )}
        </div>

        <div className="w-24 text-right whitespace-nowrap tabular-nums">
          {usdValue ? (
            <span className="text-sm opacity-70 cofhe-text-primary inline-block w-full text-right">{usdValue}</span>
          ) : (
            <span className="text-sm opacity-0 select-none inline-block w-full text-right">$0.00</span>
          )}
        </div>

        <KeyboardArrowRightIcon className="w-5 h-5 cofhe-text-primary opacity-60 flex-shrink-0" />
      </div>
    </div>
  );
};
